import { Grid, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpenseHistory from './ExpenseHistory';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { AdminAuthContext } from '../../../Admin/Contexts/AdminAuthContext';

const useStyles = makeStyles(() => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
}));

const ReasonForSplittingForm = (props) => {
    const { index, expense, originalExpenseEntries, isReimbursement, isViewOnly, mode } = props;
    const { control, errors, setValue, watch, clearErrors } = useFormContext();
    const { isClinicianReimEntryUser } = useContext(AdminAuthContext);
    const watchExpenseEntries = watch(`expenseEntries`);
    const [required, setRequired] = useState({ required: false });
    const expenseStatus = watch(`expenseEntries[${index}].status`);
    const requireClinicianFacingComment =
        expenseStatus === 'NonReimbursable' || expenseStatus === 'NeedMoreInformation';
    const isNew = !expense.id;
    const isReadOnly = (() => {
        // If my tab original db expense is posted disable
        if (
            expense.id &&
            expense.id > 0 &&
            originalExpenseEntries &&
            originalExpenseEntries.length > 0
        ) {
            const originalForPage = originalExpenseEntries.find((entry) => entry.id === expense.id);
            if (
                (originalForPage && originalForPage.status === 'Posted' && isViewOnly) ||
                (originalForPage.status === 'PendingSubmission' && mode !== 'payment')
            ) {
                // If my tab original db expense is posted disable
                return true;
            }
        }
        return false;
    })();

    const watchSplitTransactionComment = watch(`expenseEntries[${index}].splitTransactionComment`);
    const watchClinicianFacingComments = watch(`expenseEntries[${index}].clinicianFacingComments`);
    const classes = useStyles();
    const hasMountedRef = useRef(false);

    useEffect(() => {
        // All split tabs stay mounted. Skip the initial sync so each loaded comment
        // stays on its own expense. Copying on mount let a stale split comment
        // overwrite a newly saved primary comment.
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        for (let indexLoop = 0; indexLoop < watchExpenseEntries.length; ++indexLoop) {
            if (indexLoop !== index) {
                // If its not my comment
                let originalForPage = null;
                if (
                    originalExpenseEntries &&
                    originalExpenseEntries.length > 0 &&
                    watchExpenseEntries[indexLoop].id &&
                    watchExpenseEntries[indexLoop].id > 0
                ) {
                    originalForPage = originalExpenseEntries.find(
                        (entry) => entry.id === watchExpenseEntries[indexLoop].id
                    );
                }
                if (
                    !originalExpenseEntries ||
                    !originalForPage ||
                    (originalForPage && originalForPage.status !== 'Posted')
                ) {
                    // If the other tab original db expense is not posted update
                    if (
                        watchExpenseEntries[indexLoop].splitTransactionComment !==
                        watchSplitTransactionComment
                    ) {
                        setValue(
                            `expenseEntries[${indexLoop}].splitTransactionComment`,
                            watchSplitTransactionComment
                        );
                    }
                    if (
                        watchExpenseEntries[indexLoop].clinicianFacingComments !==
                        watchClinicianFacingComments
                    ) {
                        setValue(
                            `expenseEntries[${indexLoop}].clinicianFacingComments`,
                            watchClinicianFacingComments
                        );
                    }
                }
            }
        }
    }, [watchClinicianFacingComments, watchSplitTransactionComment, setValue]);

    useEffect(() => {
        if (watchExpenseEntries.length > 1) {
            setRequired({ required: true });
        } else {
            setRequired({ required: false });
            clearErrors(`expenseEntries[0].splitTransactionComment`);
        }
    }, [watchExpenseEntries.length]);

    const reSubmittedDate = useMemo(
        () =>
            expense.resubmittedDate &&
            moment.utc(expense.resubmittedDate).tz('America/New_York').format('MM/DD/YYYY'),
        [expense]
    );

    const submittedDate = useMemo(
        () =>
            expense.submittedDate &&
            moment.utc(expense.submittedDate).tz('America/New_York').format('MM/DD/YYYY'),
        [expense]
    );

    const createdDate = useMemo(
        () =>
            expense.created &&
            moment.utc(expense.created).tz('America/New_York').format('MM/DD/YYYY'),
        [expense]
    );
    const precodedCreatedDate = useMemo(
        () =>
            expense.precodedCreated &&
            moment.utc(expense.precodedCreated).tz('America/New_York').format('MM/DD/YYYY'),
        [expense]
    );

    const submittedByText = useMemo(() => {
        // Merged CC Expense text
        if (
            Object.prototype.hasOwnProperty.call(expense, 'wasMergedWithAPrecoded') &&
            expense.wasMergedWithAPrecoded === true
        ) {
            return `Pending CC Submitted by ${expense.precodedCreatedUserName} on ${precodedCreatedDate} `;
        }

        // Injested CC Expense text
        if (
            expense.expenseClass === ExpenseClassEnum.IngestedCCTransaction ||
            expense.expenseClass === ExpenseClassEnum.Vault
        ) {
            return `Imported by ${expense.createdUserName} on ${createdDate} `;
        }

        // Reimbursement Expense text
        if (expense.expenseClass === ExpenseClassEnum.Reimbursement) {
            if (!submittedDate) {
                return `Created by ${expense.createdUserName} on ${createdDate} `;
            }
            if (reSubmittedDate) {
                // Find the most recent ReSubmitted status
                const reSubmittedStatusHistory = expense.statusHistory
                    ? expense.statusHistory
                          .filter(
                              (entry) =>
                                  entry.newStatus === 'Submitted' &&
                                  entry.oldStatus === 'NeedMoreInformation'
                          )
                          .reduce(
                              (latest, current) =>
                                  new Date(current.created) > new Date(latest.created)
                                      ? current
                                      : latest,
                              { created: '1970-01-01T00:00:00.000' }
                          )
                    : null; // Default to an old date

                return `Submitted by ${expense.createdUserName} on ${submittedDate} \n Resubmitted by ${reSubmittedStatusHistory?.createdBy} on ${reSubmittedDate} `;
            }
            return `Submitted by ${expense.createdUserName} on ${submittedDate} `;
        }

        // TDS-9113 Default value. This may need adjusting in an upcoming story doing the other expense types
        // for now leaving the messagining as it was before for the other types as TDS-9113 was Reimbursement
        return `Submitted by ${expense.createdUserName} on ${createdDate} `;
    }, [expense, createdDate, precodedCreatedDate, submittedDate, reSubmittedDate]);

    return (
        <div>
            <div>
                {expense.expenseClass !== ExpenseClassEnum.Vault && (
                    <Typography variant="h5" component="h5" className={classes.formHeader}>
                        Transaction Reason
                    </Typography>
                )}
                <Grid container spacing={3}>
                    {expense.expenseClass !== ExpenseClassEnum.Vault && (
                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].splitTransactionComment`}
                                defaultValue={expense.splitTransactionComment || ''}
                                rules={{
                                    required: required.required
                                        ? 'Split transaction reason is required'
                                        : false,
                                }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        multiline
                                        inputProps={{ maxLength: 100 }}
                                        minRows={3}
                                        id="split-transaction-details"
                                        onChange={onChange}
                                        value={value}
                                        label="Split Transaction Reason"
                                        placeholder="Enter your split transaction details here"
                                        {...required}
                                        disabled={isViewOnly || isReadOnly}
                                        error={Boolean(
                                            errors.expenseEntries?.[index]?.splitTransactionComment
                                        )}
                                        helperText={
                                            errors.expenseEntries?.[index]?.splitTransactionComment
                                                ?.message
                                        }
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    height: 'auto', // Allow auto height
                                                    fontSize: '14px',
                                                    padding: '10px 12px !important',
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                marginTop: '0',
                                            },
                                            '& .MuiInputLabel-shrink': {
                                                transform:
                                                    'translate(0, 1.5px) scale(0.75) !important',
                                            },
                                            '& .MuiInputBase-root::before': {
                                                borderBottom: 'none !important',
                                            }, // Removes default underline
                                            '& .MuiInputBase-root::after': {
                                                borderBottom: 'none !important',
                                            }, // Prevents focus underline
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {isReimbursement && (
                        <>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={`expenseEntries[${index}].clinicianFacingComments`}
                                    defaultValue={expense.clinicianFacingComments}
                                    rules={{
                                        required: requireClinicianFacingComment
                                            ? 'Clinician facing comments are required'
                                            : false,
                                    }}
                                    render={({ onChange, value }) => (
                                        <ExpenseTextField
                                            required={requireClinicianFacingComment}
                                            multiline
                                            inputProps={{ maxLength: 500 }}
                                            minRows={3}
                                            id="clinicianFacingComments"
                                            onChange={onChange}
                                            value={value}
                                            label="Comments from LT to Clinicians"
                                            error={Boolean(
                                                errors.expenseEntries?.[index]
                                                    ?.clinicianFacingComments
                                            )}
                                            disabled={
                                                isViewOnly ||
                                                isReadOnly ||
                                                index > 0 ||
                                                isClinicianReimEntryUser
                                            }
                                            helperText={
                                                errors.expenseEntries?.[index]
                                                    ?.clinicianFacingComments?.message
                                                    ? errors.expenseEntries?.[index]
                                                          ?.clinicianFacingComments?.message
                                                    : value && `${value.length}/500`
                                            }
                                            placeholder="Provide notes or instructions for clinicians"
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        height: 'auto', // Allow auto height
                                                        fontSize: '14px',
                                                        padding: '10px 12px !important',
                                                    },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    marginTop: '0',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    transform:
                                                        'translate(0, 1.5px) scale(0.75) !important',
                                                },
                                                '& .MuiInputBase-root::before': {
                                                    borderBottom: 'none !important',
                                                }, // Removes default underline
                                                '& .MuiInputBase-root::after': {
                                                    borderBottom: 'none !important',
                                                }, // Prevents focus underline
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={`expenseEntries[${index}].commentsFromClinician`}
                                    defaultValue={expense.commentsFromClinician || ''}
                                    render={({ onChange, value }) => (
                                        <ExpenseTextField
                                            multiline
                                            inputProps={{ maxLength: 500 }}
                                            minRows={3}
                                            id=""
                                            onChange={onChange}
                                            value={value}
                                            label="Comments from Clinician"
                                            placeholder="Comments entered by the clinician"
                                            disabled
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        height: 'auto', // Allow auto height
                                                        fontSize: '14px',
                                                        padding: '10px 12px !important',
                                                    },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    marginTop: '0',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    transform:
                                                        'translate(0, 1.5px) scale(0.75) !important',
                                                },
                                                '& .MuiInputBase-root::before': {
                                                    borderBottom: 'none !important',
                                                }, // Removes default underline
                                                '& .MuiInputBase-root::after': {
                                                    borderBottom: 'none !important',
                                                }, // Prevents focus underline
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                    {!isNew && (
                        <Grid item xs={12}>
                            <Grid container alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                                        {submittedByText}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" justifyContent="flex-end">
                                        <ExpenseHistory mode={mode} expenseId={expense.id} />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </div>
        </div>
    );
};

export default ReasonForSplittingForm;
